import { useState } from 'react';
import type { CwSyncOutcome } from '@catalyst-psa/shared';

import { PageHeader } from '../../components/layout/PageHeader.js';
import { formatDateTime } from '../../utils/date.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { useAuth } from '../../auth/authContext.js';
import { useBackfillOnboarding } from '../onboarding/api.js';
import { useCwConnection, useCwDatasets, useCwSequentialSchedule } from './api.js';
import { ConnectionEditor } from './ConnectionEditor.js';
import { CwCustomerFilter } from './CwCustomerFilter.js';
import { CwSyncActivityLog } from './CwSyncActivityLog.js';
import { CwSyncErrorLog } from './CwSyncErrorLog.js';
import { DatasetCard } from './DatasetCard.js';
import { SequentialScheduleCard } from './SequentialScheduleCard.js';

const OUTCOME_TONES: Record<CwSyncOutcome, 'green' | 'amber' | 'red' | 'blue'> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
  running: 'blue',
};

interface DetailItemProps {
  label: string;
  value: string;
}

function DetailItem({ label, value }: DetailItemProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

export function ConnectWisePage(): JSX.Element {
  const { isAdmin } = useAuth();
  const connectionQuery = useCwConnection();
  const datasetsQuery = useCwDatasets();
  const sequentialScheduleQuery = useCwSequentialSchedule();

  const [editingConnection, setEditingConnection] = useState(false);

  const connection = connectionQuery.data;
  const datasets = datasetsQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="ConnectWise"
        description="Read-only connector that mirrors ConnectWise records into Catalyst."
      />

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Connection</h2>
          <div className="flex items-center gap-2">
            {connectionQuery.isLoading ? null : (
              <>
                {connection?.managedInDatabase ? <Badge tone="blue">Managed in app</Badge> : null}
                <Badge tone={connection?.configured ? 'green' : 'gray'}>
                  {connection?.configured ? 'Configured' : 'Not configured'}
                </Badge>
              </>
            )}
            {isAdmin && connection && !editingConnection ? (
              <Button size="sm" variant="secondary" onClick={() => setEditingConnection(true)}>
                Edit
              </Button>
            ) : null}
          </div>
        </div>

        {connectionQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading connection…</p>
        ) : connectionQuery.isError ? (
          <p className="text-sm text-rose-600">Failed to load connection status.</p>
        ) : (
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailItem label="Site URL" value={connection?.siteUrl ?? '—'} />
            <DetailItem label="Company ID" value={connection?.companyId ?? '—'} />
            <DetailItem label="Client ID" value={connection?.clientId ?? '—'} />
            <DetailItem
              label="Look-back days"
              value={connection ? String(connection.lookbackDays) : '—'}
            />
            <DetailItem label="Last sync" value={formatDateTime(connection?.lastSyncAt ?? null)} />
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Last outcome</dt>
              <dd className="mt-1">
                {connection?.lastSyncOutcome ? (
                  <Badge tone={OUTCOME_TONES[connection.lastSyncOutcome]}>
                    {connection.lastSyncOutcome}
                  </Badge>
                ) : (
                  <span className="text-sm font-medium text-slate-900">—</span>
                )}
              </dd>
            </div>
            <DetailItem label="Last watermark" value={connection?.lastSyncSince ?? '—'} />
            <DetailItem label="Message" value={connection?.lastSyncMessage ?? '—'} />
          </dl>
        )}

        {isAdmin && editingConnection && connection ? (
          <ConnectionEditor connection={connection} onClose={() => setEditingConnection(false)} />
        ) : null}
      </div>

      <CwCustomerFilter
        connection={connection ?? null}
        isAdmin={isAdmin}
        configured={connection?.configured ?? false}
      />

      {sequentialScheduleQuery.data ? (
        <SequentialScheduleCard
          schedule={sequentialScheduleQuery.data}
          isAdmin={isAdmin}
          configured={connection?.configured ?? false}
        />
      ) : null}

      <div className="mb-6">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Datasets</h2>
        {datasetsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading datasets…</p>
        ) : datasetsQuery.isError ? (
          <p className="text-sm text-rose-600">Failed to load datasets.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {datasets.map((dataset) => (
              <DatasetCard
                key={dataset.dataset}
                dataset={dataset}
                isAdmin={isAdmin}
                configured={connection?.configured ?? false}
              />
            ))}
          </div>
        )}
      </div>

      <CwSyncActivityLog isAdmin={isAdmin} />

      <CwSyncErrorLog isAdmin={isAdmin} />

      {isAdmin ? <OnboardingBackfillCard /> : null}
    </div>
  );
}

/**
 * One-off admin tool (parked here so it stays out of the day-to-day screens once the
 * ConnectWise connector is retired): creates an onboarding record for every ACTIVE
 * project that doesn't have one, linking the project (and the customer's most recent
 * draft agreement) as if the onboarding automation had created them. Lets CW-imported
 * projects follow the PSA close-out workflow (Ready to Bill / Live).
 */
function OnboardingBackfillCard(): JSX.Element {
  const backfill = useBackfillOnboarding();
  const result = backfill.data;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-700">Backfill onboarding records</h2>
          <p className="mt-1 text-xs text-slate-500">
            Creates an onboarding record for every active project that doesn&apos;t have one,
            linking the project&apos;s opportunity and the customer&apos;s most recent draft
            agreement as if the onboarding automation had run. Never creates opportunities —
            projects without a matching opportunity are skipped and listed below. Steps are
            marked done/skipped and the delivery phase is set to &quot;In Implementation&quot;.
            Safe to run repeatedly — projects that already have an onboarding record are
            skipped.
          </p>
        </div>
        <Button
          variant="secondary"
          isLoading={backfill.isPending}
          onClick={() => backfill.mutate()}
        >
          Run backfill
        </Button>
      </div>
      {result ? (
        <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          Scanned {result.scanned} active projects · created {result.created} onboarding records
          ({result.agreementsLinked} with a draft agreement linked) · skipped {result.skipped}{' '}
          that already existed.
        </p>
      ) : null}
      {result && result.noOpportunity.length > 0 ? (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <p className="font-medium">
            Skipped {result.noOpportunity.length} project
            {result.noOpportunity.length === 1 ? '' : 's'} with no matching opportunity — link
            an opportunity to the project, then re-run:
          </p>
          <ul className="mt-1 list-inside list-disc">
            {result.noOpportunity.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {backfill.isError ? (
        <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">
          Backfill failed.{' '}
          {backfill.error instanceof Error ? backfill.error.message : 'Please try again.'}
        </p>
      ) : null}
    </div>
  );
}
